<script lang="ts">
import PaymentMethodsPanel from '@/components/payment/PaymentMethodsPanel.vue'
import { useApmButtons } from '@/composables/useApmButtons'
import { useDepositOptions } from '@/composables/useDepositOptions'
import { useOneClickPayment } from '@/composables/useOneClickPayment'
import { usePromotions } from '@/composables/usePromotions'
import { useCheckoutBasket } from '@/plugins/useCheckoutBasket'
import { usePaymentProvider } from '@/plugins/usePaymentProvider'
import { useSolidgate } from '@/plugins/useSolidgate'
import { useTailwind } from '@/plugins/useTailwind'
import { AnalyticsService } from '@/services/analytics'
import type { Deposit, DepositBonusOption, Nullable, Pence } from '@/types'
import { createPaymentError } from '@/utils/checkout-helpers'
import {
  calculateBonusCredit,
  extractBonusPercent,
  getBonusTierForAmount,
  resolvePromotionForCustomAmount,
} from '@/utils/deposit-utils'
import {
  PaymentProviders,
  ProductTypes,
  type TProductSku,
} from '@/utils/helper-objects'
import { closeAllModals } from '@/utils/utils'
import PaymentStageSolidgate from '@/views/Checkout/CheckoutStages/PaymentStageSolidgate.vue'
import { TextButton } from '@goodlifeplus/component-library'
import { ThemeCurrency } from '@goodlifeplus/themes'
import type { SolidgatePaymentIntentResponse } from '@solidgate/vue-sdk'
import {
  type UseDebounceFnReturn,
  useDebounceFn,
  useStepper,
} from '@vueuse/core'
import { type Component, defineComponent, useTemplateRef } from 'vue'

import CustomerSafetyToast, {
  CUSTOMER_SAFETY_TOAST_OPTIONS,
} from '../toasts/CustomerSafetyToast.vue'
import DepositOptionCard from './deposit/DepositOptionCard.vue'
import PersonalisedAmountCard from './deposit/PersonalisedAmountCard.vue'

const PaymentStageSolidgateComponent = PaymentStageSolidgate as Component

export default defineComponent({
  name: 'DepositModal',
  components: {
    DepositOptionCard,
    PersonalisedAmountCard,
    PaymentMethodsPanel,
    PaymentStageSolidgate: PaymentStageSolidgateComponent,
    TextButton,
  },
  props: {
    buttonText: {
      type: String,
      default: 'Deposit',
    },
  },
  setup(): Record<string, any> {
    const tailwind = useTailwind()
    const {
      mount,
      handlePaymentAuthorized,
      handlePaymentError,
      errors: paymentErrors,
      clearErrors: clearPaymentErrors,
    } = usePaymentProvider(PaymentProviders.Solidgate)
    const { updatePaymentIntent } = useSolidgate()
    const stepper = useStepper(['payment', 'success'])
    const { loadPromotions } = usePromotions()

    return {
      isLg: tailwind.isLg,
      checkoutBasket: useCheckoutBasket(),
      mount,
      handlePaymentAuthorized,
      handlePaymentError,
      updatePaymentIntent,
      clearPaymentErrors,
      paymentErrors,
      stepper,
      // Keep refs in parent for reactive Solidgate integration
      paymentStageRef: useTemplateRef<any>('paymentStageRef'),
      ...useApmButtons(),
      useDebounceFn,
      // Spread depositOptions to unwrap refs/computed values
      ...useDepositOptions(),
      loadPromotions,
      ...useOneClickPayment({ clearPaymentErrors }),
    }
  },
  data() {
    return {
      isPaymentReady: false,
      isAddNewCardOpen: false,
      isPaymentProcessing: false,
      selectedAmount: '',
      paymentIntentData: null as Nullable<SolidgatePaymentIntentResponse>,
      debouncedUpdatePayment: null as Nullable<UseDebounceFnReturn<() => void>>,
      debouncedPendingAmountApply: null as Nullable<
        UseDebounceFnReturn<() => void>
      >,
      isCustomAmountFocused: false,
      selectedOptionSku: null as Nullable<TProductSku>,
      pendingCustomAmount: '',
      lastSubmittedAmount: 0,
      lastSubmittedSku: null as Nullable<TProductSku>,
      isPaymentUpdating: false,
      successSelectedOption: null as Nullable<DepositBonusOption>,
    }
  },
  computed: {
    selectedDeposit(): Deposit {
      const items = this.checkoutBasket.basket.items
      const defaultDepositOption =
        this.firstCatalogBundle || this.highestCostCatalogBundle

      if (!defaultDepositOption) {
        return {
          __type: 'Deposit' as const,
          sku: 'CUSTOM' as TProductSku,
          amount: 0,
          bonus_credit_percent: 0,
        }
      }

      const defaultDeposit = this.createDepositFromOption(
        defaultDepositOption,
        defaultDepositOption.price_minor ?? 0
      )

      if (items.length === 0) {
        return defaultDeposit
      }

      const firstItem = items[0]
      if (firstItem.type !== ProductTypes.Deposit) {
        return defaultDeposit
      }

      return firstItem.product as Deposit
    },
    paymentButtonLabel(): string {
      const baseLabel = this.buttonText || 'Deposit'
      if (this.isOneClickPayOpen) {
        return `${baseLabel} | •••• ${this.oneClickPay?.last_4_digits}`
      }
      return this.selectedDeposit?.bonus_credit_percent > 0
        ? `${baseLabel} & Get Bonus`
        : baseLabel
    },
    selectedOption(): DepositBonusOption | null {
      if (!this.selectedDeposit?.sku) {
        return null
      }
      return this.depositAmounts.get(this.selectedDeposit.sku) ?? null
    },
    successBonusPence(): number {
      const deposit = this.selectedDeposit
      const option = this.successSelectedOption ?? this.selectedOption
      if (!deposit || !option) {
        return 0
      }

      if (
        option.type === 'promotion' &&
        option.promotion_details?.match_percentage !== undefined
      ) {
        const matchMultiplier = option.promotion_details.match_percentage / 100
        const uncapped = deposit.amount * matchMultiplier
        const cap = option.promotion_details.max_bonus_minor
        return cap ? Math.min(uncapped, cap) : uncapped
      }

      return deposit.amount * deposit.bonus_credit_percent
    },
    oneClickSummaryItems(): Array<{ label: string; amount: number }> {
      const items: Array<{ label: string; amount: number }> = [
        { label: 'Deposit Amount', amount: this.selectedDeposit.amount / 100 },
      ]
      if (this.successBonusPence > 0) {
        items.push({
          label: 'Bonus Credit',
          amount: this.successBonusPence / 100,
        })
      }
      return items
    },
    paymentForm() {
      return {
        ...this.$userStore.user,
        amount: this.selectedDeposit.amount,
      }
    },
    currentBonusPercent(): number {
      return this.selectedDeposit.bonus_credit_percent
    },
    bonusTierInfo() {
      const pendingAmount = this.pendingCustomAmount
      if (!pendingAmount || parseInt(pendingAmount) <= 0) {
        return {
          percent: 0,
          nextTier: null,
          remainingPence: 0,
          nextBonusPercent: 0,
        }
      }
      return getBonusTierForAmount(
        parseInt(pendingAmount),
        this.customWalletOption
      )
    },
    showNextTierHint(): boolean {
      return (
        this.currentBonusPercent >= 0 &&
        this.bonusTierInfo.remainingPence > 0 &&
        this.bonusTierInfo.nextBonusPercent > this.currentBonusPercent
      )
    },
    selectedCoinConfig(): { totalCoins: number; silverCoins: number } | null {
      const option = this.depositOptionsWithCoinCount.find(
        (
          opt: DepositBonusOption & {
            coinCount: number
            silverCoinCount: number
            displayAmount: number
            bonusPercent: number
          }
        ) => opt.sku === this.selectedDeposit.sku
      )
      return option
        ? { totalCoins: option.coinCount, silverCoins: option.silverCoinCount }
        : null
    },
    minimumDepositForPoints(): number {
      return this.$theme.currency === ThemeCurrency.CAD ? 10 : 5
    },
  },
  watch: {
    pendingCustomAmount(value: string, old: string) {
      if (
        value !== old &&
        this.selectedOptionSku === this.customWalletOption?.sku &&
        !this.isPaymentUpdating
      ) {
        void this.debouncedPendingAmountApply?.()
      }

      const numericValue = parseFloat(`${value}`) * 100
      const depositAmount = (Number(numericValue) / 100).toFixed(2)
      const previousDepositAmount = (this.selectedDeposit.amount / 100).toFixed(
        2
      )

      if (previousDepositAmount === depositAmount) {
        return
      }

      this.isPaymentReady = false
    },
    isPaymentReady() {
      if (this.paymentStageRef?.updateAPMButtonState && this.isPaymentReady) {
        this.paymentStageRef.updateAPMButtonState(this.isPaymentReady)
      }
    },
    isAddNewCardOpen() {
      if (this.isAddNewCardOpen) {
        const depositModalBottomAnchor = document.getElementById(
          'deposit-modal-bottom-anchor'
        ) as Nullable<HTMLElement>

        if (depositModalBottomAnchor) {
          setTimeout(() => {
            depositModalBottomAnchor.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            })
          }, 500)
        }
      }
    },
  },
  async mounted() {
    await this.loadDepositOptions({ force: true })

    const updatePayment = this.updatePayment.bind(this)
    this.debouncedUpdatePayment = useDebounceFn(updatePayment, 750)

    this.debouncedPendingAmountApply = useDebounceFn(() => {
      if (this.selectedOptionSku === this.customWalletOption?.sku) {
        void this.applyCustomAmount()
      }
    }, 1200)
  },
  methods: {
    cancelPayment() {
      this.isAddNewCardOpen = false
      this.isOneClickPayOpen = false
    },
    isOptionSelected(option: DepositBonusOption): boolean {
      return this.selectedOptionSku === option.sku
    },
    createDepositFromOption(
      option: DepositBonusOption,
      customAmount: Pence = 0
    ): Deposit {
      let amount = 0

      if (option.product_type === 'wallet_top_up') {
        amount = customAmount
      } else if (option.type === 'promotion') {
        amount =
          customAmount > 0 ? customAmount : (option.max_deposit_minor ?? 0)
      } else {
        amount = option.price_minor ?? 0
      }

      if (option.type === 'promotion' && amount === 0) {
        amount = option.max_deposit_minor ?? 0
      } else if (amount < 1 && option.type !== 'promotion') {
        amount = Math.max(1, amount)
      }

      let bonusDecimal = extractBonusPercent(option)

      if (
        option.product_type === 'wallet_top_up' &&
        option.components?.[0]?.payload?.bonus_tiers
      ) {
        bonusDecimal = calculateBonusCredit(
          amount,
          this.customWalletOption,
          this.depositAmounts
        )
      }

      const deposit: Deposit = {
        __type: 'Deposit',
        sku: option.sku,
        amount: amount,
        bonus_credit_percent: bonusDecimal,
      }
      return deposit
    },
    resetState() {
      this.checkoutBasket.clearBasket()
      this.isAddNewCardOpen = false
      this.reset()
      this.isPaymentReady = false
      this.isPaymentProcessing = false
      this.paymentIntentData = null
      this.selectedOptionSku = null
      this.pendingCustomAmount = ''
      this.lastSubmittedAmount = 0
      this.lastSubmittedSku = null
      this.isPaymentUpdating = false
      this.stepper.goTo('payment')
    },
    async handleOpenSheetOrModal(openSheetOrModal: () => void) {
      if (this.isApiUnavailable || !this.hasAnyDepositOptions) {
        this.$alertStore.addErrorAlert({
          title: 'Deposit Unavailable',
          message:
            'We are unable to load deposit options at this time. Please try again later or contact support if this issue persists.',
          buttonText: 'OK',
        })
        return
      }

      if (
        this.$customerSafetyStore.hasPlayerSafetyLockout ||
        this.$customerSafetyStore.checkCustomerSpendingLimit(100)
      ) {
        this.$toast(
          {
            component: CustomerSafetyToast,
            props: {
              safetyMeasure:
                this.$customerSafetyStore.getFirstActiveCustomerSafety(100),
            },
          },
          CUSTOMER_SAFETY_TOAST_OPTIONS
        )
        return
      }

      // Close all other open modals
      closeAllModals()

      openSheetOrModal()
      this.resetState()

      const pendingReservation = this.visibleDepositOptions.find(
        (opt: DepositBonusOption) => opt.status === 'pending_reservation'
      )
      const defaultSku =
        pendingReservation?.sku ?? this.highestCostCatalogBundle?.sku

      if (!defaultSku) {
        console.error('[DepositModal] No default SKU available')
        return
      }

      if (!this.depositAmounts.has(defaultSku)) {
        console.error('[DepositModal] Default SKU not found:', defaultSku)
        return
      }

      const defaultOption = this.depositAmounts.get(defaultSku)
      const defaultAmount =
        defaultOption?.type === 'promotion'
          ? (defaultOption.promotion_details?.default_deposit_minor ??
            defaultOption.max_deposit_minor ??
            0)
          : (defaultOption?.price_minor ?? 0)

      this.pendingCustomAmount = Math.floor(defaultAmount / 100).toString()

      await this.addToBasket(defaultSku, defaultAmount)
      await this.mountPaymentProvider()

      AnalyticsService.trackBeginCheckout(this.checkoutBasket.basket.items)
    },
    async mountPaymentProvider() {
      // Wait for DOM refs to be available before mounting Solidgate
      await this.$nextTick()

      setTimeout(async () => {
        try {
          await this.mount(ProductTypes.Deposit)
          this.isPaymentReady = true
        } catch (error) {
          console.error(
            `Failed to initialize ${PaymentProviders.Solidgate}:`,
            error
          )
          this.isPaymentReady = false
        }

        if (this.paymentStageRef?.updateAPMButtonState) {
          this.paymentStageRef.updateAPMButtonState(this.isPaymentReady)
        }
      }, 500)
    },
    async handlePaymentResult(payment: any) {
      this.isPaymentProcessing = false

      if (payment && payment.success) {
        if (!payment.data) {
          this.handlePaymentError(
            createPaymentError(
              "Failed to retrieve payment information. The payment was processed but we couldn't confirm your purchase.",
              payment.orderId
            )
          )
          return
        }

        void this.$userStore.fetchDetails(true)

        // Snapshot the option used for this payment before refreshing state
        this.successSelectedOption = this.selectedOption

        this.stepper.goTo('success')
      } else {
        if (payment.data?.error) {
          this.handlePaymentError(
            createPaymentError(payment.data.error, payment.orderId)
          )
        }
      }
    },
    async runPendingRefresh(close: (force: boolean) => void) {
      try {
        await this.loadDepositOptions({ force: true })
        await this.loadPromotions({ force: true })

        this.isOneClickPayOpen = false
        void this.$nextTick(() => {
          close(true)
        })
      } catch (e) {
        console.error('Failed to refresh deposit/promo data after payment:', e)
      }
    },
    async updatePayment() {
      if (!this.isPaymentReady || !this.paymentStageRef) {
        return
      }

      if (!this.paymentIntentData || !this.paymentStageRef.formMounted) {
        await this.paymentStageRef.reinitializePayment()
        return
      }

      const currentAmount = this.selectedDeposit.amount
      const currentSku = this.selectedDeposit.sku
      if (
        currentAmount === this.lastSubmittedAmount &&
        currentSku === this.lastSubmittedSku
      ) {
        return
      }

      this.clearPaymentErrors()

      try {
        this.isPaymentReady = false

        const result = await this.paymentStageRef.updateCreditAmount(0)

        if (!result.success) {
          const errorMessage =
            result.error ||
            'Payment processing error. Please contact customer support.'
          this.handlePaymentError({ message: errorMessage })
        } else {
          this.lastSubmittedAmount = currentAmount
          this.lastSubmittedSku = currentSku
        }
      } catch (error) {
        console.error('Failed to update payment intent:', error)
        return
      } finally {
        this.isPaymentReady = true
      }
    },
    async processPayment(oneClick: boolean = false) {
      try {
        this.clearPaymentErrors()

        let success: boolean = false
        let result: any = null

        if (oneClick) {
          // For one-click payments, set processing state immediately
          result = await this.paymentStageRef?.processOneClickPayment()
          success = result?.success ?? false
        } else {
          result = await this.paymentStageRef?.processPayment()
          success = result?.submitted ?? false
        }

        if (!success) {
          const errorMessage =
            result.error ||
            result.errorMessage ||
            `${result?.timedOut ? 'Payment processing timed out.' : 'An unexpected error occurred.'} Please contact customer support.`

          this.handlePaymentError({ message: errorMessage })
          this.isPaymentProcessing = false
        }
      } catch (e: any) {
        this.handlePaymentError({
          message:
            e.message ?? 'An unexpected error occurred. Please try again.',
        })
        this.isPaymentProcessing = false
      }
    },
    addToBasketValue(amountInPounds: number) {
      const currentPounds = Math.floor(this.selectedDeposit.amount / 100)
      const newPounds = currentPounds + amountInPounds
      this.pendingCustomAmount = newPounds.toString()
      void this.applyCustomAmount()
    },
    async applyCustomAmount() {
      const pounds = parseInt(this.pendingCustomAmount, 10)

      if (isNaN(pounds) || pounds < 1) {
        return
      }

      const pence = pounds * 100

      if (pence === this.selectedDeposit.amount) {
        return
      }

      if (pence < this.minDepositAmount) {
        this.handlePaymentError({
          message: `The minimum deposit amount is ${this.$n(this.minDepositPounds, 'currency')}.<br>Please enter a higher amount.`,
        })
        return
      }

      if (pence > this.maxDepositAmount) {
        this.handlePaymentError({
          message: `The maximum deposit amount is ${this.$n(this.maxDepositPounds, 'currency')}.<br>Please enter a lower amount.`,
        })
        return
      }

      const promoOption = resolvePromotionForCustomAmount(
        pence,
        this.depositAmounts
      )

      if (promoOption) {
        await this.addToBasket(promoOption.sku, pence)
        return
      }

      const walletSku = this.customWalletOption?.sku
      if (walletSku) {
        await this.addToBasket(walletSku, pence)
      }
    },
    handleCustomAmountFocus() {
      this.isCustomAmountFocused = true
      this.selectedOptionSku = this.customWalletOption?.sku ?? null
    },
    handleCustomAmountBlur() {
      if (this.pendingCustomAmount) {
        void this.applyCustomAmount()
      }
      this.isCustomAmountFocused = false
    },
    async addToBasket(depositSku: TProductSku, amount: Pence = 0) {
      amount = Math.round(amount)

      const maxAmount = this.maxDepositAmount
      if (amount > maxAmount) {
        this.handlePaymentError({
          message: `The maximum deposit amount is ${this.$n(maxAmount / 100, 'currency')}.<br>Please select a lower amount.`,
        })
        return
      }

      this.selectedOptionSku = depositSku

      let deposit: Deposit | null = null

      if (this.depositAmounts.has(depositSku)) {
        const option = this.depositAmounts.get(depositSku) as DepositBonusOption
        deposit = this.createDepositFromOption(option, amount)
      } else {
        for (const depositOption of this.depositAmounts.values()) {
          if (depositOption.price_minor === amount) {
            deposit = this.createDepositFromOption(depositOption, amount)
          }
        }
      }

      if (!deposit) {
        const walletSku = this.customWalletOption?.sku
        deposit = {
          __type: 'Deposit',
          sku: walletSku ?? depositSku,
          amount: amount,
          bonus_credit_percent: calculateBonusCredit(
            amount,
            this.customWalletOption,
            this.depositAmounts
          ),
        }
      }

      const minAmount = this.minDepositAmount
      if (deposit.amount < minAmount) {
        deposit.amount = minAmount
      }

      this.checkoutBasket.clearBasket()
      this.checkoutBasket.addItem(deposit)
      this.selectedAmount = (deposit.amount / 100).toFixed(2)

      this.selectedAmount = Math.floor(deposit.amount / 100).toString()

      const amountInPounds = Math.floor(deposit.amount / 100)
      this.pendingCustomAmount = amountInPounds.toString()

      this.isPaymentReady = true
      await this.debouncedUpdatePayment?.()
    },
  },
})
</script>

<template>
  <AppModal
    id="deposit-checkout-modal"
    button-class="w-full"
    close-button-class="right-6 top-5 z-10"
    class="max-w-[585px] md:w-[585px] w-full"
    :allow-dismissal="!isPaymentProcessing"
    :dialog-mode="false"
    :disable-close="isOneClickPayOpen"
    @close="isOneClickPayOpen = false"
  >
    <template #button="{ openModal }">
      <slot
        name="button"
        :open-modal="() => handleOpenSheetOrModal(openModal)"
        :has-any-deposit-options="hasAnyDepositOptions"
      >
        <AppTextButton
          label="Deposit"
          :disabled="!hasAnyDepositOptions"
          @click="handleOpenSheetOrModal(openModal)"
        />
      </slot>
    </template>

    <template #default="{ closeModal }">
      <!-- Payment Processing -->
      <div
        v-show="isPaymentProcessing"
        class="flex w-full flex-col items-center justify-center rounded-lg border border-slate-300 bg-pure-white p-6"
      >
        <div class="spinner-2 size-12"></div>
        <div class="mt-8 text-center text-2xl font-bold">
          Your order is being processed...
          <p class="text-sm text-gray-500">
            Please do not refresh or close this page.
          </p>
        </div>
      </div>

      <!-- Payment Step -->
      <div
        v-if="stepper.current.value === 'payment'"
        v-show="!isPaymentProcessing"
        class="flex flex-col gap-4 rounded-lg border border-slate-300 bg-pure-white p-6"
      >
        <template v-if="!isOneClickPayOpen">
          <h1 class="text-xl font-bold">Deposit</h1>

          <p class="font-bold">Select Amount</p>

          <div class="flex flex-wrap gap-3">
            <DepositOptionCard
              v-for="depositOption in depositOptionsWithCoinCount"
              :key="depositOption.sku"
              :option="depositOption"
              :is-selected="isOptionSelected(depositOption)"
              @select="
                addToBasket(
                  depositOption.sku,
                  depositOption.type === 'promotion'
                    ? (depositOption.promotion_details?.default_deposit_minor ??
                        depositOption.max_deposit_minor ??
                        0)
                    : 0
                )
              "
            />
          </div>

          <!-- First deposit points message -->
          <div
            v-if="firstDepositXpEligible && selectedDeposit.amount >= 500"
            class="text-sm text-center py-2 px-3 bg-primary-lighter rounded-lg border border-primary"
          >
            <p class="font-bold">
              First Deposit Bonus: Earn 500 points or more!
            </p>
            <p class="text-xs mt-1">
              Complete your first deposit of
              {{ $n(minimumDepositForPoints, 'currency') }} or more to unlock
              your bonus points.
            </p>
          </div>

          <!-- Personalised Amount Card -->
          <PersonalisedAmountCard
            :custom-wallet-option="customWalletOption"
            :is-selected="selectedOptionSku === customWalletOption?.sku"
            :pending-amount="pendingCustomAmount"
            :min-deposit-pounds="minDepositPounds"
            :max-deposit-pounds="maxDepositPounds"
            :min-deposit-amount="minDepositAmount"
            :max-deposit-amount="maxDepositAmount"
            :is-payment-updating="isPaymentUpdating"
            :selected-deposit="selectedDeposit"
            :deposit-amounts="depositAmounts"
            :deposit-options-with-coin-count="depositOptionsWithCoinCount"
            :selected-coin-config="selectedCoinConfig"
            @select="
              () => {
                selectedOptionSku = customWalletOption?.sku ?? null
                handleCustomAmountFocus()
              }
            "
            @update:pending-amount="pendingCustomAmount = $event"
            @add-value="addToBasketValue"
          />

          <p class="text-sm">
            For legal reasons 100% of deposit and bonus must be spent on the
            site before withdrawal.
          </p>

          <!-- Bonus tier messaging (visible for all deposit types) -->
          <div v-if="showNextTierHint" class="text-sm text-center mt-2">
            <p class="font-bold text-blue-800">
              Deposit
              {{ $n(bonusTierInfo.remainingPence / 100, 'currency') }}
              more for next bonus tier and unlock
              {{ $n(bonusTierInfo.nextBonusPercent, 'percent') }} credit!
            </p>
          </div>
        </template>

        <!-- Payment Methods -->
        <PaymentMethodsPanel
          :payment-errors="paymentErrors"
          :is-payment-ready="isPaymentReady"
          :is-payment-processing="isPaymentProcessing"
          :is-add-new-card-open="isAddNewCardOpen"
          :is-one-click-pay-open="isOneClickPayOpen"
          :summary-items="oneClickSummaryItems"
          :one-click-pay="oneClickPay"
          @update:is-add-new-card-open="isAddNewCardOpen = $event"
          @update:is-one-click-pay-open="isOneClickPayOpen = $event"
          @clear-payment-errors="clearPaymentErrors"
        >
          <template #apmButtons>
            <div
              v-show="isPaymentReady && !isOneClickPayOpen"
              class="flex gap-2 min-h-[40px]"
            >
              <div ref="applePayButton" class="flex-1 h-[40px]" />
              <div ref="googlePayButton" class="flex-1 h-[40px]" />
            </div>

            <!-- Loading state -->
            <p
              v-if="!isPaymentReady"
              class="italic w-full text-center text-sm text-gray-500 min-h-[40px]"
            >
              Loading Payment Options...
            </p>
          </template>

          <template #paymentForm="{ showForm }">
            <PaymentStageSolidgate
              v-if="$userStore.user"
              v-show="showForm"
              ref="paymentStageRef"
              :model-value="paymentForm"
              class="payment-stage-solidgate"
              :show-errors="false"
              :payment-errors="paymentErrors"
              :product="selectedDeposit"
              :form-valid="true"
              :form-type="paymentFormType"
              :apm-config="apmConfig"
              :auto-update-intent="false"
              :can-one-click="true"
              @payment-authorized="handlePaymentResult"
              @payment-error="handlePaymentError($event)"
              @payment-processing="isPaymentProcessing = $event"
              @payment-intent:updated="paymentIntentData = $event"
            >
              <template #optin>
                <div class="flex gap-2 mt-4">
                  <TextButton
                    :variant="UI.TextButton.Variant.Tertiary"
                    :size="UI.TextButton.Size.Large"
                    label="Back"
                    @click="cancelPayment"
                  />
                  <TextButton
                    :variant="UI.TextButton.Variant.Primary"
                    :size="UI.TextButton.Size.Large"
                    :label="paymentButtonLabel"
                    :disabled="
                      !isPaymentReady ||
                      isPaymentProcessing ||
                      isPaymentUpdating
                    "
                    class="flex-1"
                    @click="processPayment(isOneClickPayOpen)"
                  />
                </div>
              </template>
            </PaymentStageSolidgate>
          </template>
        </PaymentMethodsPanel>

        <span id="deposit-modal-bottom-anchor" />
      </div>

      <!-- Success Step -->
      <div
        v-if="stepper.current.value === 'success'"
        v-show="!isPaymentProcessing"
        class="flex flex-col justify-center gap-4 rounded-lg border border-slate-300 bg-pure-white p-6"
      >
        <h1 class="text-2xl font-bold">Payment Confirmed</h1>

        <p class="font-bold text-center w-full">
          You Have Deposited
          {{ $n(selectedDeposit.amount / 100, 'currency') }}
        </p>

        <img
          src="@/assets/images/successful-payment.png"
          alt="Payment Success Illustration"
        />

        <hr />

        <div class="flex border-b flex-col border-b-slate-200">
          <table class="w-full">
            <tbody>
              <tr>
                <td class="pb-3 pr-3 text-left">Deposit Amount:</td>
                <td class="pb-3 text-right font-bold">
                  {{ $n(selectedDeposit.amount / 100, 'currency') }}
                </td>
              </tr>
              <tr v-if="successBonusPence > 0">
                <td class="pb-3 pr-3 text-left">{{ BUSINESS_NAME }} Credit:</td>
                <td class="pb-3 text-right font-bold">
                  {{ $n(successBonusPence / 100, 'currency') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <TextButton
          :variant="UI.TextButton.Variant.Tertiary"
          :size="UI.TextButton.Size.Large"
          label="Dismiss"
          class="min-w-full"
          @click="runPendingRefresh(closeModal)"
        />
      </div>
    </template>
  </AppModal>
</template>

<style lang="scss" scoped>
[id^='apple-pay-button-'],
[id^='google-pay-button-'] {
  @apply w-full !important;

  &:empty {
    @apply hidden;
  }

  > * {
    @apply w-full !important;
  }
}

.payment-stage-solidgate {
  :deep(.payment-container) {
    @apply border border-slate-300 bg-primary-lighter p-3 rounded-lg;
  }
}
</style>
